import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Check } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';

const Wrapper = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid #E8ECF0;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  transition: background 0.15s, border-color 0.15s;
  max-width: 220px;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
    border-color: #D0D6DC;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TenantLabel = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  box-shadow: 0 8px 32px rgba(0, 66, 66, 0.12);
  z-index: 200;
  padding: 4px 0;
`;

const DropdownTitle = styled.div`
  padding: 10px 14px 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-transform: none;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.06)' : 'none')};
  border: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(2, 145, 239, 0.08);
  }
`;

const OptionName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default function TenantSwitcher() {
  const { t } = useTranslation();
  const { switchableTenants, canSwitchTenant, activeTenantId, switchTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!canSwitchTenant) return null;

  const active = switchableTenants.find((t) => t.id === activeTenantId);
  const activeName = active?.name || t('tenantSwitcher.unknownTenant');

  const handleSelect = async (tenantId) => {
    if (tenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await switchTenant(tenantId);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <Wrapper ref={ref}>
      <Trigger
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        title={activeName}
      >
        <Building2 size={16} />
        <TenantLabel>{activeName}</TenantLabel>
        <ChevronDown size={14} color="#A0A8B4" />
      </Trigger>
      {open && (
        <Dropdown>
          <DropdownTitle>{t('tenantSwitcher.title')}</DropdownTitle>
          {switchableTenants.map((tenant) => {
            const isActive = tenant.id === activeTenantId;
            return (
              <Option
                key={tenant.id}
                type="button"
                $active={isActive}
                onClick={() => handleSelect(tenant.id)}
              >
                <OptionName>{tenant.name}</OptionName>
                {isActive && <Check size={14} color="#0291EF" />}
              </Option>
            );
          })}
        </Dropdown>
      )}
    </Wrapper>
  );
}
